import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Input from '../components/Input';
import Button from '../components/Button';

export const MIN_PASSWORD_LENGTH = 8; // mirrors passwordService.ts on the backend

/**
 * Serves two flows from one screen:
 *  - forced (mustChangePassword) — after a first login or an admin reset. The
 *    app routes here and offers no way out but logging out; the backend refuses
 *    every other route until it's satisfied.
 *  - voluntary — reached from the header, with a Cancel back to the dashboard.
 */
export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, mustChangePassword, changePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Checked here for instant feedback; the backend enforces the same rules.
  const localValidationError = (): string | null => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      return `New password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    }
    if (!/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      return 'New password must contain at least one letter and one number';
    }
    if (newPassword !== confirmPassword) return 'New passwords do not match';
    if (newPassword === currentPassword) return 'New password must be different from your current password';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = localValidationError();
    if (validationError) return setError(validationError);

    setSaving(true);
    setError('');
    try {
      await changePassword(currentPassword, newPassword);
      setDone(true);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-gray-800">
          {mustChangePassword ? 'Choose a new password' : 'Change Password'}
        </h1>
        {mustChangePassword ? (
          <p className="text-gray-600 mt-2 text-sm">
            Your password was set by an administrator. Pick a new one that only you know before continuing
            {user?.email ? ` as ${user.email}` : ''}.
          </p>
        ) : (
          <p className="text-gray-600 mt-2 text-sm">Update the password for {user?.email}.</p>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mt-5 text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <Input
            id="currentPassword"
            label={mustChangePassword ? 'Current (temporary) password' : 'Current password'}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
          <Input
            id="newPassword"
            label="New password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <Input
            id="confirmPassword"
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <p className="text-xs text-gray-500">
            At least {MIN_PASSWORD_LENGTH} characters, including a letter and a number.
          </p>

          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={saving} disabled={done} className="flex-1">
              {mustChangePassword ? 'Set password & continue' : 'Change password'}
            </Button>
            {mustChangePassword ? (
              // No "skip" — the only alternative to setting a password is
              // ending the session.
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  logout();
                  navigate('/login', { replace: true });
                }}
              >
                Log out
              </Button>
            ) : (
              <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
